import { useState } from 'react'
import {
  Button,
  Card,
  Collapse,
  Divider,
  FormGroup,
  NumericInput,
  Switch,
  H6,
} from '@blueprintjs/core'
import LabelWithTooltip from './LabelWithTooltip'

export default function AdvancedSettings({
  advancedSettingsOpen,
  setAdvancedSettingsOpen,
  seedInputRef,
  nmfRunsInputRef,
  snmfUseHardAssignmentRef,
  snmfBalanceToleranceRef,
  algorithmsSelected,
}) {
  const showNmfRuns = algorithmsSelected?.includes('NMF')
  const showSnmfSettings = algorithmsSelected?.includes('SNMF')
  const [snmfUseHardAssignment, setSnmfUseHardAssignment] = useState(true)

  return (
    <div>
      <Button
        minimal
        fill
        alignText="left"
        text="Advanced Settings"
        icon={advancedSettingsOpen ? 'chevron-down' : 'chevron-right'}
        rightIcon="settings"
        onClick={() => setAdvancedSettingsOpen(prev => !prev)}
        style={{ marginBottom: advancedSettingsOpen ? 10 : 0 }}
      />
      <Collapse isOpen={advancedSettingsOpen} keepChildrenMounted={true}>
        <Card elevation={0} style={{ padding: '15px', backgroundColor: '#f5f8fa' }}>
          <div>
          <H6 style={{ marginBottom: 8 }}>General</H6>
          <FormGroup
            labelFor="seed"
            label={
              <LabelWithTooltip
                label="Random Seed"
                tooltip="Controls randomization for all algorithms. Different seeds produce different solutions. Use the same seed to reproduce results."
              />
            }
            helperText="Change the seed to generate different solutions"
          >
            <NumericInput
              id="seed"
              leftIcon="random"
              min={0}
              defaultValue={0}
              inputRef={seedInputRef}
              fill
            />
          </FormGroup>

          {showNmfRuns && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <H6 style={{ marginBottom: 8 }}>NMF Settings</H6>
              <FormGroup
                labelFor="nmf-runs"
                label={
                  <LabelWithTooltip
                    label="Number of Runs"
                    tooltip="NMF is run multiple times with different initializations. The solution with the most evenly distributed segment sizes is chosen. More runs = more stable results but slower computation."
                  />
                }
                helperText="More runs increase stability but take longer (1-100)"
              >
                <NumericInput
                  id="nmf-runs"
                  leftIcon="social-media"
                  min={1}
                  max={100}
                  defaultValue={40}
                  inputRef={nmfRunsInputRef}
                  fill
                />
              </FormGroup>
            </>
          )}

          {showSnmfSettings && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <H6 style={{ marginBottom: 8 }}>SNMF Settings</H6>
              <FormGroup
                labelFor="snmf-hard-assignment"
                label={
                  <LabelWithTooltip
                    label="Assignment Mode"
                    tooltip="Hard Assignment: Uses strict capacity constraints to ensure segments have exactly equal total weight. Soft Assignment: Assigns to the most probable cluster, producing better cluster quality but looser weight balance."
                  />
                }
                helperText="Hard: strict weight balance. Soft: better cluster quality."
              >
                <Switch
                  id="snmf-hard-assignment"
                  checked={snmfUseHardAssignment}
                  onChange={e => setSnmfUseHardAssignment(e.target.checked)}
                  innerLabelChecked="Hard"
                  innerLabel="Soft"
                  inputRef={snmfUseHardAssignmentRef}
                  large
                />
              </FormGroup>
              {snmfUseHardAssignment && (
                <FormGroup
                  labelFor="snmf-balance-tolerance"
                  label={
                    <LabelWithTooltip
                      label="Balance Tolerance (%)"
                      tooltip="Allows segments to vary by ± this percentage from perfect balance. For example, 3% means each segment can be 97-103% of the target weight. Lower values enforce stricter balance but may sacrifice cluster quality."
                    />
                  }
                  helperText="Lower = stricter balance (1-90%)"
                >
                  <NumericInput
                    id="snmf-balance-tolerance"
                    leftIcon="percentage"
                    min={1}
                    max={90}
                    stepSize={0.5}
                    minorStepSize={0.1}
                    defaultValue={30}
                    inputRef={snmfBalanceToleranceRef}
                    fill
                  />
                </FormGroup>
              )}
            </>
          )}
          </div>
        </Card>
      </Collapse>
    </div>
  )
}
